import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/cn'
import type { ChatSubjectKind } from '@/types/domain'

/* What sits above the thread on the chat page (0052).
 *
 * The page reads a list on the left and one conversation on the right, and the
 * list can be scrolled away from the selected row — so without this the right
 * half is an unexplained column of bubbles. It names the record, says which
 * KIND of record it is, states that the conversation is internal, and offers
 * the one way out of the conversation and into the thing it is about.
 *
 * Two statements of "internal", deliberately: a chip beside the title for the
 * reader who already knows, and a banner underneath for the one who does not.
 * External participants are structurally absent from this surface (FR-46) —
 * nothing typed here can reach a client — and that is a fact worth repeating
 * on a screen where somebody is about to paste a cost breakdown.
 *
 * There is NO overflow menu, and its absence is a decision rather than an
 * omission. The reference design carries a `…` beside the record link, but
 * every action this surface has is already somewhere else: editing and
 * deleting are per MESSAGE and live in ChatPanel's own row menu, and the read
 * watermark moves by itself. A menu button opening onto nothing, or onto
 * entries that answer 404, is worse than no button — so it is added the day
 * there is a conversation-level action to put in it.
 */

export interface ChatThreadHeaderProps {
  kind: ChatSubjectKind
  subjectId: string
  /** The record's number, or the id's head while the two lists load. */
  title: string
  /** The client the record is for, absent until the lists resolve. */
  clientName?: string
}

export function ChatThreadHeader({
  kind,
  subjectId,
  title,
  clientName,
}: ChatThreadHeaderProps) {
  const { t } = useTranslation()

  /* "John Smith · Quote conversation", or just "Quote conversation" while the
     client is unresolved. A shorter true line beats "undefined · …", and the
     two are separate keys rather than one with an optional interpolation
     because a translator needs to move the separator, not delete it. */
  const subtitle = clientName
    ? kind === 'quote'
      ? t('chat.threadSubtitle.quote', { client: clientName })
      : t('chat.threadSubtitle.case', { client: clientName })
    : kind === 'quote'
      ? t('chat.conversationKind.quote')
      : t('chat.conversationKind.case')

  return (
    <>
      <header className="border-line flex shrink-0 items-start gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {/* Headings are Regular, never bold — the record identifier earns the
              emphasis weight instead, which is one of the four roles 600 is
              reserved for. */}
          <h2 className="text-text-heading truncate text-[15px] leading-tight font-semibold tracking-[-0.2px]">
            {title}
          </h2>
          <p className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-text-muted truncate text-[12.5px] font-light">
              {subtitle}
            </span>
            <span
              className={cn(
                'bg-attention-soft text-attention shrink-0 rounded-[var(--radius-sm)]',
                'px-1.5 py-0.5 text-[11px] leading-none font-medium',
              )}
            >
              {t('chat.internal')}
            </span>
          </p>
        </div>

        {/* A link rather than a button: the record is a place, and a reader who
            wants it in a second tab should get it the ordinary way. */}
        <Link
          to={kind === 'quote' ? `/quotes/${subjectId}` : `/cases/${subjectId}`}
          className={cn(
            'border-brand-border text-brand hover:bg-brand-soft inline-flex shrink-0',
            'h-[30px] items-center rounded-md border px-3 text-[12.5px]',
            'pointer-coarse:h-11 transition-colors duration-[var(--dur-fast)]',
            'ease-[var(--ease-standard)]',
          )}
        >
          {kind === 'quote' ? t('chat.viewQuote') : t('chat.viewCase')}
        </Link>
      </header>

      {/* Persistent, not dismissible. It is one line on a screen whose whole
          risk is somebody forgetting who can read it. */}
      <p
        className={cn(
          'bg-attention-soft text-attention m-3 mb-0 flex shrink-0 items-center gap-2',
          'rounded-[var(--radius-md)] px-3 py-2.5 text-[12.5px] font-normal',
        )}
      >
        <Info size={15} aria-hidden className="shrink-0" />
        {t('chat.internalBanner')}
      </p>
    </>
  )
}
